export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6

export const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

export type DayKey = (typeof DAY_KEYS)[number]

interface WeekdayLabel {
  key: DayKey
  short: string
  full: string
  long: string
  compact: string
}

export const DAY_LABELS = {
  sunday: 'Domingo',
  monday: 'Segunda-feira',
  tuesday: 'Terça-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sábado',
} as const satisfies Record<DayKey, string>

export const WEEKDAY_LABELS = {
  0: {
    key: 'sunday',
    short: 'Dom',
    full: DAY_LABELS.sunday,
    long: 'domingo',
    compact: 'domingo',
  },
  1: {
    key: 'monday',
    short: 'Seg',
    full: DAY_LABELS.monday,
    long: 'segunda-feira',
    compact: 'segunda',
  },
  2: {
    key: 'tuesday',
    short: 'Ter',
    full: DAY_LABELS.tuesday,
    long: 'terça-feira',
    compact: 'terça',
  },
  3: {
    key: 'wednesday',
    short: 'Qua',
    full: DAY_LABELS.wednesday,
    long: 'quarta-feira',
    compact: 'quarta',
  },
  4: {
    key: 'thursday',
    short: 'Qui',
    full: DAY_LABELS.thursday,
    long: 'quinta-feira',
    compact: 'quinta',
  },
  5: {
    key: 'friday',
    short: 'Sex',
    full: DAY_LABELS.friday,
    long: 'sexta-feira',
    compact: 'sexta',
  },
  6: {
    key: 'saturday',
    short: 'Sáb',
    full: DAY_LABELS.saturday,
    long: 'sábado',
    compact: 'sábado',
  },
} as const satisfies Record<WeekdayIndex, WeekdayLabel>

export const DAY_KEY_BY_WEEKDAY = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
} as const satisfies Record<WeekdayIndex, DayKey>

export const WEEKDAY_BY_DAY_KEY = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
} as const satisfies Record<DayKey, WeekdayIndex>

export const WEEKDAY_ORDER_MONDAY_FIRST = [
  WEEKDAY_BY_DAY_KEY.monday,
  WEEKDAY_BY_DAY_KEY.tuesday,
  WEEKDAY_BY_DAY_KEY.wednesday,
  WEEKDAY_BY_DAY_KEY.thursday,
  WEEKDAY_BY_DAY_KEY.friday,
  WEEKDAY_BY_DAY_KEY.saturday,
  WEEKDAY_BY_DAY_KEY.sunday,
] as const satisfies readonly WeekdayIndex[]

function isWeekdayIndex(value: number): value is WeekdayIndex {
  return Number.isInteger(value) && value >= 0 && value < DAY_KEYS.length
}

export function getDayKeyFromWeekday(weekday: number): DayKey | undefined {
  if (!isWeekdayIndex(weekday)) {
    return undefined
  }

  return DAY_KEY_BY_WEEKDAY[weekday]
}

export function getWeekdayFromDayKey(dayKey: DayKey): WeekdayIndex {
  return WEEKDAY_BY_DAY_KEY[dayKey]
}

export function getWeekdayMeta(weekday: number): WeekdayLabel | undefined {
  if (!isWeekdayIndex(weekday)) {
    return undefined
  }

  return WEEKDAY_LABELS[weekday]
}

export function getWeekdayShortLabel(weekday: number): string {
  return getWeekdayMeta(weekday)?.short ?? ''
}

export function getWeekdayFullLabel(weekday: number): string {
  return getWeekdayMeta(weekday)?.full ?? ''
}

export function getWeekdayLongLabel(weekday: number): string {
  return getWeekdayMeta(weekday)?.long ?? ''
}

export function getWeekdayCompactLabel(weekday: number): string {
  return getWeekdayMeta(weekday)?.compact ?? ''
}
